import { Box } from '@chakra-ui/react';
import {
  ContextualTipId,
  useContextualTip,
  useContextualTipContext,
} from '@resistapp/client/contexts/contextual-tip-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TimeSpanOption, useMonthSelection } from '../../hooks/use-month-selection';
import { IntegratedTextSelect } from '../selects/integrated-text-select';
import { ContextualTip } from '../tooltips/contextual-tips/contextual-tip';

export function MonthSelector() {
  const { selectedSiteDatum, metricMode } = useOverviewContext();
  const { queryFilters } = useSampleDataContext();

  const [selectActive, setSelectActive] = useState(false);
  const { options, selectedOption } = useMonthSelection({
    isDisabled: (month: Date, months: Date[] | undefined) => Boolean(selectedSiteDatum && !months?.includes(month)),
  });

  const anchorRef = useRef<HTMLDivElement>(null);
  const { visible, message, dismiss, markInteraction, animationDelay, showDismiss } = useContextualTip(
    ContextualTipId.OVERVIEW_SELECT_PREVIOUS_MONTHS,
    metricMode,
  );

  const { setMonthStable } = queryFilters;
  const handleChange = useCallback(
    (value: TimeSpanOption | null) => {
      if (!value) return;
      if (value.value === 'latest') {
        setMonthStable(null, null);
      } else {
        setMonthStable(new Date(value.value), null);
      }
      markInteraction();
    },
    [setMonthStable, markInteraction],
  );

  const { setTipEnabled } = useContextualTipContext();

  // Enable/disable the contextual tip based on whether there are multiple months to choose from
  useEffect(() => {
    setTipEnabled(ContextualTipId.OVERVIEW_SELECT_PREVIOUS_MONTHS, options.length > 1);
  }, [options.length, setTipEnabled]);

  return (
    <Box
      ref={anchorRef}
      onClick={() => {
        if (!selectActive) {
          setSelectActive(true);
          markInteraction();
        }
      }}
    >
      <IntegratedTextSelect<TimeSpanOption>
        options={options}
        value={selectedOption}
        size="sm"
        menuIsOpenByDefault={selectActive}
        onChange={handleChange}
        onMenuClose={() => {
          setSelectActive(false);
        }}
      >
        <span style={{ wordWrap: 'normal', whiteSpace: 'nowrap' }}>{selectedOption.label}</span>
      </IntegratedTextSelect>
      {visible && (
        <ContextualTip
          anchorRef={anchorRef}
          message={message || ''}
          onDismiss={dismiss}
          placement="bottom"
          animationDelay={animationDelay}
          arrowPosition="left"
          showDismiss={showDismiss}
        />
      )}
    </Box>
  );
}
